import type {
  ChartChoice,
  ChartDimensionChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartMetricChoice,
  ChartMetricsChoice,
  ChartMetricTransposedChoice,
  ChartMetricVariantsChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  DimensionsMap,
  DimensionsValuesMap,
  MetricsMap,
  TransposeMetric,
  ElementsDescriptionsMap,
  ChartChoicesMap,
  MetricsVariantsMap,
  ChartDimensionValueChoice,
  ChartDimensionValueOfChoice,
  ChartMetricVariantsTransposedChoice,
  ChartChoiceChartAxis,
  ElementDescriptionMetric,
  ChartDimensionAxisChoice,
  DimensionAxisType,
  ChartCardTableColumnsChoice,
  MetricChoice
} from '../internalTypes'
import type {
  ChartCardChartDefinition,
  BaseElementDescription,
  ChartCardTableColumnsProps,
  ChartChoiceOption,
  ChartChoiceOptions,
  ChartChoiceOptionsOf,
  ChartDataRow,
  ChartDimensionValue,
  ChartDimensionValues,
  ChartFilterValues,
  ChartSelections,
  ChartVariantValue,
  ChartVariantValues,
  ColumnsProps,
  DataKeyMap,
  Dimension,
  DimensionValuesSortType,
  Metric,
  MetricFilterType,
  MetricsVariants,
  VariableOptionsPropertyType,
  VariantNewMetrics
} from '../types'
import {
  assertNever,
  strictObjectEntries,
  errorLocal,
  generateIdForSelectionOthers,
  transposedMetricColumn,
  objectKeys,
  setDifference,
  objectValues
} from './generics'
import {
  FORMULA_MAX_LENGTH,
  FORMULA_MAX_NESTING_DEPTH,
  FORMULA_MAX_OPERATOR_COUNT,
  SelectedOthers,
  TableColumnsChoice
} from '../constants'
import { createChartChoiceValueOptions } from './options'
import { filterDimensionValues } from './data'
import type { ThemeColor } from '@precis-digital/kurama/theme'
import type { Row, SortingFnOption } from '@tanstack/react-table'
import { getFooterMetricAggregation } from '../components/table/utils'
import { getColumnDataKey, getColumnId, isColumnWithAccessorKey } from '../components/table/utils/columnKeys'
import { elementDescriptionIsDimension, elementDescriptionIsMetric } from './elementDescription'
import { oneOfFilter } from '../components/table/utils/filterAndSortingFns'
import type { ChartCardTableColumnDef } from '../components/table/tanstack'
import { compile, type MathNode, parse, type SymbolNode } from 'mathjs'

export const generateChoiceId = <DataKey extends string>(parentKey: DataKey | undefined, choiceId: DataKey): DataKey =>
  parentKey == null ? choiceId : (`${parentKey}.${choiceId}` as DataKey)

export const extractIdFromChoiceId = <DataKey extends string>(choiceId: DataKey): DataKey =>
  choiceId.split('.').pop() as DataKey

/**
 * Create the default chart variant value props single choice from the variant id.
 *
 * The {@link ChartVariantValue} type is used to customise the variant value options used in single selection choices.
 * this function creates the default one (all values available).
 * @param variantId - The variant id.
 * @param dimensionsMap - The dimensions map.
 * @returns The chart variant values.
 */
const createDefaultChartVariantValuePropsFromVariantId = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>(
  variantId: DataKey,
  dimensionsMap: DimensionsMap<DataKey, DataRow>
): ChartVariantValue<'props', DataKey, DataRow> => {
  const dimension = dimensionsMap[variantId]
  return {
    type: 'variantValue',
    id: variantId,
    dimension: variantId,
    change: {
      change: 'internal',
      options: 'all'
    },
    label: dimension?.label,
    description: dimension?.description
  }
}

/**
 * Create the default chart variant values props multiple choice from the variant id.
 *
 * The {@link ChartVariantValues} type is used to customise the variant value options used in multiple selection choices.
 * this function creates the default one (all values available).
 * @param variantId - The variant id.
 * @param dimensionsMap - The dimensions map.
 * @returns The chart variant values.
 */
const createDefaultChartVariantValuesPropsFromVariantId = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>(
  variantId: DataKey,
  dimensionsMap: DimensionsMap<DataKey, DataRow>
): ChartVariantValues<'props', DataKey, DataRow> => {
  const dimension = dimensionsMap[variantId]
  return {
    type: 'variantValues',
    id: variantId,
    dimension: variantId,
    change: {
      change: 'internal',
      options: 'all'
    },
    label: dimension?.label,
    description: dimension?.description
  }
}

/**
 * Create the default chart choice options from the options list and a map of options descriptions.
 *
 * A secondary preferred map of options descriptions can be provided to override the primary map.
 * @param options - The options list.
 * @param map - The map of options descriptions.
 * @param preferredMap - The preferred map of options descriptions.
 * @returns The chart choice options.
 */
export const createDefaultChartChoiceOptions = <DataKey extends string>(
  options: DataKey[],
  map: DataKeyMap<DataKey, BaseElementDescription>,
  preferredMap?: DataKeyMap<DataKey, Omit<BaseElementDescription, 'value'>>
): Array<ChartChoiceOption<DataKey>> =>
  options.map<ChartChoiceOption<DataKey>>((option) => {
    const { label, description, color } = (preferredMap?.[option] ?? map[option] ?? {}) as Omit<
      BaseElementDescription,
      'value'
    >
    return { value: option, label, description, color }
  })

const checkExistingVariantChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  variant,
  dimensionsMap,
  otherChoicesMap
}: {
  variant: DataKey | ChartVariantValue<'props', DataKey, DataRow> | ChartVariantValues<'props', DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}):
  | {
      type: 'invalid'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'undefined'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'variantValue'
      variantChoice: ChartVariantValueChoice<DataKey, DataRow>
      newMetrics: VariantNewMetrics<DataKey>
      fullMetricsList: DataKey[]
    }
  | {
      type: 'variantValues'
      variantChoice: ChartVariantValuesChoice<DataKey, DataRow>
      newMetrics: VariantNewMetrics<DataKey>
      fullMetricsList: DataKey[]
    } => {
  if (typeof variant === 'string') {
    const variantChoice = otherChoicesMap[variant]
    if (variantChoice == null) {
      return { type: 'undefined' }
    }
    if (variantChoice.type !== 'variantValue' && variantChoice.type !== 'variantValues') {
      return { type: 'invalid' }
    }

    const { newMetrics, variantsMap } = dimensionsMap[variantChoice.dimension] as MetricsVariants<
      'choice',
      DataKey,
      DataRow
    >
    if (variantChoice.type === 'variantValue') {
      return {
        type: 'variantValue',
        variantChoice,
        newMetrics,
        fullMetricsList: variantChoice.change.options.flatMap(
          ({ value }) => Object.values(variantsMap[value]) as DataKey[]
        )
      }
    }
    return {
      type: 'variantValues',
      variantChoice,
      newMetrics,
      fullMetricsList: variantChoice.change.options.flatMap(
        ({ value }) => Object.values(variantsMap[value]) as DataKey[]
      )
    }
  }

  return { type: 'undefined' }
}
/**
 * Create the default chart variant value props single choice from the variant id or the variant value props {@link ChartVariantValue}.
 *
 * The {@link ChartVariantValue} type is used to customise the variant value options used in single selection choices.
 * this function creates the default one (all values available).
 *
 * If a variant id is passed,
 * the {@link createDefaultChartVariantValuePropsFromVariantId} function is used to create the variant value props.
 *
 * if a {@link ChartVariantValue} is passed, it is also validated.
 *
 * @param variantIdOrValue - The variant id or the variant value props.
 * @param dimensionsMap - The dimensions map.
 * @returns
 * * The chart variant value choice {@link ChartVariantValueChoice}.
 * * The new metrics {@link VariantNewMetrics}.
 */
export const createDefaultChartVariantValueChoiceFromIdOrValue = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  parentKey,
  choiceAxis,
  variantIdOrValue,
  dimensionsMap,
  otherChoicesMap
}: {
  parentKey: DataKey
  choiceAxis: ChartChoiceChartAxis
  variantIdOrValue: DataKey | ChartVariantValue<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}):
  | {
      type: 'invalid'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'undefined'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'variantValue'
      variantChoice: ChartVariantValueChoice<DataKey, DataRow>
      newMetrics: VariantNewMetrics<DataKey>
      fullMetricsList: DataKey[]
    }
  | {
      type: 'variantValues'
      variantChoice: ChartVariantValuesChoice<DataKey, DataRow>
      newMetrics: VariantNewMetrics<DataKey>
      fullMetricsList: DataKey[]
    } => {
  if (variantIdOrValue == null) {
    return { type: 'undefined' }
  }

  const existingVariantChoice = checkExistingVariantChoice<DataKey, DataRow>({
    variant: variantIdOrValue,
    dimensionsMap,
    otherChoicesMap
  })
  if (existingVariantChoice.type !== 'undefined') {
    return existingVariantChoice
  }

  const variantValue =
    typeof variantIdOrValue === 'string'
      ? createDefaultChartVariantValuePropsFromVariantId<DataKey, DataRow>(variantIdOrValue, dimensionsMap)
      : variantIdOrValue
  const variant = dimensionsMap[variantValue.dimension] as MetricsVariants<'choice', DataKey, DataRow> | undefined
  if (variant?.type !== 'variant') {
    return { type: 'invalid' }
  }

  const {
    id: variantId,
    change: { options: optionsOrAll, ...changeBase },
    ...variantValueBase
  } = variantValue

  const { label, description, newMetrics, variantsMap, descriptions: valuesDescriptionsMap } = variant
  const options = optionsOrAll === 'all' ? objectKeys(variantsMap) : optionsOrAll

  return {
    type: 'variantValue',
    variantChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, variantId),
      label,
      description,
      ...variantValueBase,
      change: {
        ...changeBase,
        options: options.map((value) => ({ value, ...valuesDescriptionsMap?.[value] }))
      }
    },
    newMetrics,
    fullMetricsList: options.flatMap((value) => Object.values(variantsMap[value]) as DataKey[])
  }
}

/**
 * Create the chart choice options of from a list dimensions.
 *
 * The options property can be a list of options or a callback to generate them.
 *
 * If the options property is a callback, it is called with the data, the id of the choice and the dimension id.
 *
 * If the options property is a list of options, it is used directly.
 *
 * The return is the {@link ChartChoiceOptionsOf} object, a map of dimension ids with the options for each dimension.
 *
 * @param id - The id of the choice.
 * @param dimensions - The dimensions (list of ids).
 * @param optionsOr - The options property (a list of options or a callback to generate them).
 * @param otherOption - The other option (if any).
 * @param dimensionsMap - The dimensions map.
 * @param dimensionsValuesMap - The dimensions values map.
 * @param data - The chart data.
 * @returns The chart choice options of {@link ChartChoiceOptionsOf}.
 */
const createChartChoiceOptionsOfFromDimensions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  id,
  dimensions,
  optionsOr,
  sort,
  metrics,
  filter,
  otherOption,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  id: DataKey
  dimensions: DataKey[]
  optionsOr: VariableOptionsPropertyType<'props', DataKey, DataRow, string, 'optionalWithCallback', 'variable'>
  sort: DimensionValuesSortType | undefined
  metrics: DataKey[] | undefined
  filter: MetricFilterType | ChartFilterValues<DataKey, DataRow> | undefined
  otherOption: string | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): ChartChoiceOptionsOf<DataKey, string> | undefined => {
  const optionsOf: Partial<ChartChoiceOptionsOf<DataKey, string>> = {}

  for (const dimensionId of dimensions) {
    const dimension = dimensionsMap[dimensionId] as Dimension<'choice', DataKey, DataRow>

    if (typeof optionsOr === 'function') {
      let options: string[] | undefined
      try {
        options = optionsOr(data, id, dimensionId)
      } catch (error) {
        errorLocal(`createChartChoiceOptionsOfFromDimensions ${dimensionId} optionsOr callback error`, error)
      }
      if (options == null) return undefined
      const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })
      optionsOf[dimensionId] = createChartChoiceValueOptions<DataKey, DataRow>({
        dimension,
        otherOption,
        options: filteredOptions,
        sort,
        chartAvailableColors
      })
    } else if (optionsOr === 'all') {
      const options = Array.from(dimensionsValuesMap[dimensionId])
      const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })
      optionsOf[dimensionId] = createChartChoiceValueOptions<DataKey, DataRow>({
        dimension,
        otherOption,
        options: filteredOptions,
        sort,
        chartAvailableColors
      })
    } else {
      const valuesOrAll = optionsOr[dimensionId]
      const options = Array.isArray(valuesOrAll) ? valuesOrAll : Array.from(dimensionsValuesMap[dimensionId])
      const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })
      optionsOf[dimensionId] = createChartChoiceValueOptions<DataKey, DataRow>({
        dimension,
        otherOption,
        options: filteredOptions,
        sort,
        chartAvailableColors
      })
    }
  }

  return optionsOf as ChartChoiceOptionsOf<DataKey, string>
}

/**
 * Create the default chart variant values props multiple choice from the variant id or the variant value props {@link ChartVariantValues}.
 *
 * The {@link ChartVariantValues} type is used to customise the variant value options used in multiple selection choices.
 * this function creates the default one (all values available).
 *
 * If a variant id is passed,
 * the {@link createDefaultChartVariantValuesPropsFromVariantId} function is used to create the variant value props.
 *
 * if a {@link ChartVariantValues} is passed, it is also validated.
 *
 * @param variantIdOrValue - The variant id or the variant value props.
 * @param dimensionsMap - The dimensions map.
 * @returns
 * * The chart variant values choice {@link ChartVariantValuesChoice}.
 * * The new metrics {@link VariantNewMetrics}.
 */
export const createDefaultChartVariantValuesChoiceFromIdOrValue = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  parentKey,
  choiceAxis,
  variantIdOrValues,
  dimensionsMap,
  otherChoicesMap
}: {
  parentKey: DataKey
  choiceAxis: ChartChoiceChartAxis
  variantIdOrValues: DataKey | ChartVariantValues<'props', DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
}):
  | {
      type: 'invalid'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'undefined'
      variantChoice?: never
      newMetrics?: never
      fullMetricsList?: never
    }
  | {
      type: 'variantValues'
      variantChoice: ChartVariantValuesChoice<DataKey, DataRow>
      newMetrics: VariantNewMetrics<DataKey>
      fullMetricsList: DataKey[]
    } => {
  if (variantIdOrValues == null) {
    return { type: 'undefined' }
  }

  const existingVariantChoice = checkExistingVariantChoice<DataKey, DataRow>({
    variant: variantIdOrValues,
    dimensionsMap,
    otherChoicesMap
  })
  if (existingVariantChoice.type !== 'undefined') {
    if (existingVariantChoice.type === 'variantValue') {
      return { type: 'invalid' }
    }
    return existingVariantChoice
  }

  const variantValues =
    typeof variantIdOrValues === 'string'
      ? createDefaultChartVariantValuesPropsFromVariantId<DataKey, DataRow>(variantIdOrValues, dimensionsMap)
      : variantIdOrValues
  const variant = dimensionsMap[variantValues.dimension]
  if (variant?.type !== 'variant') {
    return { type: 'invalid' }
  }

  const {
    id: variantId,
    change: { options: optionsOrAll, otherOption, ...changeBase },
    ...variantValueBase
  } = variantValues

  const { label, description, newMetrics, variantsMap, descriptions: valuesDescriptionsMap } = variant
  const options = optionsOrAll === 'all' ? objectKeys(variantsMap) : optionsOrAll

  return {
    type: 'variantValues',
    variantChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, variantId),
      label,
      description,
      ...variantValueBase,
      change: {
        ...changeBase,
        options: options.map((value) => ({ value, ...valuesDescriptionsMap?.[value] })),
        otherOption
      }
    },
    newMetrics,
    fullMetricsList: options.flatMap((value) => Object.values(variantsMap[value]) as DataKey[])
  }
}

type GetDimensionValuesFromIdResult<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  WithVariantValues extends boolean
> =
  | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  | (WithVariantValues extends true ? ChartVariantValues<'props', DataKey, DataRow> : never)

const getDimensionValuesFromId = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  WithVariantValues extends boolean
>(
  dimensionId: DataKey,
  dimensionsMap: DimensionsMap<DataKey, DataRow>
): GetDimensionValuesFromIdResult<DataKey, DataRow, WithVariantValues> | undefined => {
  const dimension = dimensionsMap[dimensionId]

  if (dimension == null) {
    return undefined
  }

  const result: GetDimensionValuesFromIdResult<DataKey, DataRow, true> = {
    type: dimension.type === 'variant' ? 'variantValues' : 'values',
    id: dimensionId,
    dimension: dimensionId,
    change: {
      change: 'static',
      options: 'all'
    },
    label: dimension.label,
    description: dimension.description
  }

  return result as GetDimensionValuesFromIdResult<DataKey, DataRow, WithVariantValues>
}

const getDimensionValueFromId = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimensionId: DataKey,
  dimensionsMap: DimensionsMap<DataKey, DataRow>
): ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'> | undefined => {
  const dimension = dimensionsMap[dimensionId]

  if (dimension == null) {
    return undefined
  }

  return {
    type: 'value',
    id: dimensionId,
    dimension: dimensionId,
    change: {
      change: 'static',
      options: 'all'
    },
    label: dimension.label,
    description: dimension.description
  }
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultInvalid {
  type: 'invalid'
  dimension?: never
  dimensionChoice?: never
  dimensionValuesChoice?: never
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimension<DataKey extends string> {
  type: 'dimension'
  dimension: DataKey
  dimensionChoice?: never
  dimensionValuesChoice?: never
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimensions<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> {
  type: 'dimensions'
  dimension?: never
  dimensionChoice: ChartDimensionChoice<DataKey, DataRow>
  dimensionValuesChoice?: never
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValues<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> {
  type: 'values'
  dimension?: never
  dimensionChoice?: never
  dimensionValuesChoice: ChartDimensionValuesChoice<DataKey, DataRow>
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValuesOf<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> {
  type: 'valuesOf'
  dimension?: never
  dimensionChoice: ChartDimensionChoice<DataKey, DataRow>
  dimensionValuesChoice: ChartDimensionValuesOfChoice<DataKey, DataRow>
}

interface CreateDefaultDimensionValuesChoicesFromIdOrValuesResultVariantValues<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> {
  type: 'variantValues'
  dimension?: never
  dimensionChoice?: never
  dimensionValuesChoice: ChartVariantValuesChoice<DataKey, DataRow>
}

type CreateDefaultDimensionValuesChoicesFromIdOrValuesResult<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  WithVariantValues extends boolean
> =
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultInvalid
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimension<DataKey>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimensions<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValues<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValuesOf<DataKey, DataRow>
  | (WithVariantValues extends true
      ? CreateDefaultDimensionValuesChoicesFromIdOrValuesResultVariantValues<DataKey, DataRow>
      : never)

const createDefaultDimensionValuesChoicesFromValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionValues,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey | undefined
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionValues: ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}):
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValues<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimension<DataKey>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultInvalid => {
  const {
    id,
    change: { options: valuesOptionsOr, otherOption, ...valuesChange },
    dimension: dimensionId,
    sort,
    filter,
    ...dimensionValuesChoice
  } = dimensionValues

  if (typeof sort === 'function' && metrics != null) {
    return { type: 'invalid' }
  }

  const dimension = dimensionsMap[dimensionId]

  if (dimension == null) {
    return { type: 'invalid' }
  }

  // Polar slices use dimension-value colours; keep a values choice so the series
  // palette can be applied when the dimension definition has no descriptions.
  const isPolarChart = choiceAxis.chartType === 'pie' || choiceAxis.chartType === 'donut'
  if (dimension.descriptions == null && sort == null && valuesOptionsOr === 'all' && !isPolarChart) {
    return {
      type: 'dimension',
      dimension: dimensionId,
      dimensionChoice: undefined,
      dimensionValuesChoice: undefined
    }
  }

  let options: string[] | undefined
  if (typeof valuesOptionsOr === 'function') {
    try {
      options = valuesOptionsOr(data, id)
    } catch (error) {
      errorLocal(
        `createDefaultDimensionValuesChoicesFromIdOrValues ${dimensionId} valuesOptionsOr callback error`,
        error
      )
    }

    if (options == null) {
      return { type: 'invalid' }
    }
  } else if (valuesOptionsOr === 'all') {
    // Include static change (dashboards status/pie/donut): otherwise options stay
    // empty and createChartChoiceValueOptions never auto-assigns series colours.
    options = Array.from(dimensionsValuesMap[dimensionValues.dimension])
  } else if (Array.isArray(valuesOptionsOr)) {
    options = valuesOptionsOr
  } else {
    options =
      valuesChange.change === 'internal' || valuesChange.change === 'hybrid'
        ? Array.from(dimensionsValuesMap[dimensionValues.dimension])
        : []
  }

  const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })

  return {
    type: 'values',
    dimensionValuesChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, id),
      dimension: dimensionId,
      change: {
        ...valuesChange,
        options: createChartChoiceValueOptions<DataKey, DataRow>({
          dimension,
          otherOption,
          options: filteredOptions,
          sort: typeof sort === 'function' ? undefined : sort,
          chartAvailableColors
        }),
        otherOption
      },
      sort,
      filter,
      label: dimension.label,
      description: dimension.description,
      ...dimensionValuesChoice
    }
  }
}

const createDefaultDimensionValuesChoicesFromValuesOf = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionValuesOf,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey | undefined
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionValuesOf: ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}):
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultValuesOf<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultDimensions<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultInvalid => {
  const {
    id,
    dimension: {
      change: { options: dimensions, ...dimensionsChange },
      id: dimensionChoiceIdProp,
      ...dimensionChoice
    },
    change: { options: valuesOptionsOr, otherOption, ...valuesChange },
    sort,
    filter,
    ...dimensionValuesChoice
  } = dimensionValuesOf

  const dimensionChoiceId = generateChoiceId(parentKey, dimensionChoiceIdProp)

  if (typeof sort === 'function' && metrics != null) {
    return { type: 'invalid' }
  }

  const optionsOf = createChartChoiceOptionsOfFromDimensions<DataKey, DataRow>({
    id,
    dimensions,
    optionsOr: valuesOptionsOr,
    sort: typeof sort === 'function' ? undefined : sort,
    metrics,
    filter,
    otherOption,
    dimensionsMap,
    dimensionsValuesMap,
    data,
    chartAvailableColors
  })

  if (optionsOf == null) {
    return { type: 'invalid' }
  }

  return {
    type: 'valuesOf',
    dimensionChoice: {
      ...choiceAxis,
      id: dimensionChoiceId,
      ...dimensionChoice,
      change: {
        ...dimensionsChange,
        options: createDefaultChartChoiceOptions(dimensions, dimensionsMap)
      }
    },
    dimensionValuesChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, id),
      ...dimensionValuesChoice,
      dimension: dimensionChoiceId,
      change: {
        ...valuesChange,
        options: optionsOf
      },
      sort,
      filter
    }
  }
}

const createDefaultDimensionValuesChoicesFromVariantValues = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  parentKey,
  choiceAxis,
  metrics,
  variantValues,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey | undefined
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  variantValues: ChartVariantValues<'props', DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}):
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultVariantValues<DataKey, DataRow>
  | CreateDefaultDimensionValuesChoicesFromIdOrValuesResultInvalid => {
  const {
    id,
    change: { options: valuesOptionsOr, ...valuesChange },
    dimension: dimensionId,
    sort,
    filter,
    ...dimensionValuesChoice
  } = variantValues

  if (typeof sort === 'function' && metrics != null) {
    return { type: 'invalid' }
  }

  const dimension = dimensionsMap[dimensionId]

  if (dimension?.type !== 'variant') {
    return { type: 'invalid' }
  }

  const options: string[] = Array.isArray(valuesOptionsOr)
    ? valuesOptionsOr
    : Array.from(dimensionsValuesMap[variantValues.dimension])

  const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })

  return {
    type: 'variantValues',
    dimensionValuesChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, id),
      dimension: dimensionId,
      change: {
        ...valuesChange,
        options: createChartChoiceValueOptions<DataKey, DataRow>({
          dimension,
          otherOption: undefined,
          options: filteredOptions,
          sort: typeof sort === 'function' ? undefined : sort,
          chartAvailableColors
        })
      },
      sort,
      filter,
      label: dimension.label,
      description: dimension.description,
      color: dimension.color,
      pattern: dimension.pattern,
      ...dimensionValuesChoice
    }
  }
}

const createDefaultDimensionValueChoicesFromValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionValue,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionValue: ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): CreateDefaultDimensionValueChoicesFromIdOrValuesResult<DataKey, DataRow> => {
  const {
    id,
    change: { options: valuesOptionsOr, otherOption, ...valuesChange },
    dimension: dimensionId,
    sort,
    filter,
    ...dimensionValuesChoice
  } = dimensionValue

  const dimension = dimensionsMap[dimensionId] as Dimension<'choice', DataKey, DataRow>

  if (dimension.type === 'date' || (dimension.descriptions == null && sort == null)) {
    return {
      type: 'dimension',
      dimension: dimensionId,
      dimensionChoice: undefined,
      dimensionValueChoice: undefined
    }
  }

  let options: string[] | undefined
  if (typeof valuesOptionsOr === 'function') {
    try {
      options = valuesOptionsOr(data, id)
    } catch (error) {
      errorLocal(
        `createDefaultDimensionValueChoicesFromIdOrValues ${dimensionId} valuesOptionsOr callback error`,
        error
      )
    }

    if (options == null) {
      return { type: 'invalid' }
    }
  } else {
    options = Array.isArray(valuesOptionsOr)
      ? valuesOptionsOr
      : Array.from(dimensionsValuesMap[dimensionValue.dimension])
  }

  if (dimension == null) {
    return { type: 'invalid' }
  }

  const filteredOptions = filterDimensionValues({ dimensionId, options, metrics, filter, data })

  return {
    type: 'value',
    dimensionValueChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, id),
      dimension: dimensionId,
      change: {
        ...valuesChange,
        options: createChartChoiceValueOptions<DataKey, DataRow>({
          dimension,
          otherOption,
          options: filteredOptions,
          sort,
          chartAvailableColors
        }),
        otherOption
      },
      sort,
      filter,
      label: dimension.label,
      description: dimension.description,
      ...dimensionValuesChoice
    }
  }
}

const createDefaultDimensionValueChoicesFromValuesOf = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionValueOf,
  dimensionsMap,
  dimensionsValuesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionValueOf: ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): CreateDefaultDimensionValueChoicesFromIdOrValuesResult<DataKey, DataRow> => {
  const {
    id,
    dimension: {
      change: { options: dimensions, ...dimensionsChange },
      id: dimensionChoiceIdProp,
      ...dimensionChoice
    },
    change: { options: valuesOptionsOr, otherOption, ...valuesChange },
    sort,
    filter,
    ...dimensionValuesChoice
  } = dimensionValueOf

  const dimensionChoiceId = generateChoiceId(parentKey, dimensionChoiceIdProp)

  if (metrics == null) {
    return {
      type: 'dimensions',
      dimensionChoice: {
        ...choiceAxis,
        id: dimensionChoiceId,
        ...dimensionChoice,
        change: {
          ...dimensionsChange,
          options: createDefaultChartChoiceOptions(dimensions, dimensionsMap)
        }
      }
    }
  }

  const optionsOf = createChartChoiceOptionsOfFromDimensions<DataKey, DataRow>({
    id,
    dimensions,
    optionsOr: valuesOptionsOr,
    sort,
    metrics,
    filter,
    otherOption,
    dimensionsMap,
    dimensionsValuesMap,
    data,
    chartAvailableColors
  })

  if (optionsOf == null) {
    return { type: 'invalid' }
  }

  return {
    type: 'valueOf',
    dimensionChoice: {
      ...choiceAxis,
      id: dimensionChoiceId,
      ...dimensionChoice,
      change: {
        ...dimensionsChange,
        options: createDefaultChartChoiceOptions(dimensions, dimensionsMap)
      }
    },
    dimensionValueChoice: {
      ...choiceAxis,
      id: generateChoiceId(parentKey, id),
      ...dimensionValuesChoice,
      dimension: dimensionChoiceId,
      change: {
        ...valuesChange,
        options: optionsOf
      },
      sort,
      filter
    }
  }
}

const checkExistingDimensionValuesChoices = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  WithVariantValues extends boolean
>(
  parentKey: DataKey | undefined,
  dimensionIdOrValues:
    | DataKey
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
    | ChartVariantValues<'props', DataKey, DataRow>
    | undefined,
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
): CreateDefaultDimensionValuesChoicesFromIdOrValuesResult<DataKey, DataRow, WithVariantValues> | undefined => {
  if (typeof dimensionIdOrValues !== 'string') return undefined

  const firstChoice =
    otherChoicesMap[dimensionIdOrValues] ?? otherChoicesMap[generateChoiceId(parentKey, dimensionIdOrValues)]

  if (firstChoice != null) {
    if (firstChoice.type === 'values') {
      return { type: 'values', dimensionValuesChoice: firstChoice }
    }
    if (firstChoice.type === 'valuesOf') {
      const dimensionChoice = otherChoicesMap[firstChoice.dimension] as ChartDimensionChoice<DataKey, DataRow>
      return { type: 'valuesOf', dimensionChoice, dimensionValuesChoice: firstChoice }
    }
    return { type: 'invalid' }
  }

  return undefined
}

export const createDefaultDimensionValuesChoicesFromIdOrValues = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>,
  WithVariantValues extends boolean
>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionIdOrValues,
  dimensionsMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey | undefined
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionIdOrValues:
    | DataKey
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
    | ChartDimensionValues<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
    | (WithVariantValues extends true ? ChartVariantValues<'props', DataKey, DataRow> : never)
    | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): CreateDefaultDimensionValuesChoicesFromIdOrValuesResult<DataKey, DataRow, WithVariantValues> => {
  const existingDimensionValuesChoices = checkExistingDimensionValuesChoices<DataKey, DataRow, WithVariantValues>(
    parentKey,
    dimensionIdOrValues,
    otherChoicesMap
  )
  if (existingDimensionValuesChoices != null) {
    return existingDimensionValuesChoices
  }

  if (typeof dimensionIdOrValues === 'string') {
    dimensionIdOrValues = getDimensionValuesFromId<DataKey, DataRow, WithVariantValues>(
      dimensionIdOrValues,
      dimensionsMap
    )
  }

  if (dimensionIdOrValues == null) {
    return { type: 'invalid' }
  }

  const type = dimensionIdOrValues.type
  switch (type) {
    case 'values':
      return createDefaultDimensionValuesChoicesFromValues({
        parentKey,
        choiceAxis,
        metrics,
        dimensionValues: dimensionIdOrValues,
        dimensionsMap,
        dimensionsValuesMap,
        data,
        chartAvailableColors
      })
    case 'valuesOf':
      return createDefaultDimensionValuesChoicesFromValuesOf({
        parentKey,
        choiceAxis,
        metrics,
        dimensionValuesOf: dimensionIdOrValues,
        dimensionsMap,
        dimensionsValuesMap,
        data,
        chartAvailableColors
      })
    case 'variantValues':
      return createDefaultDimensionValuesChoicesFromVariantValues({
        parentKey,
        choiceAxis,
        metrics,
        variantValues: dimensionIdOrValues,
        dimensionsMap,
        dimensionsValuesMap,
        data,
        chartAvailableColors
      }) as CreateDefaultDimensionValuesChoicesFromIdOrValuesResult<DataKey, DataRow, WithVariantValues>
    default:
      assertNever(type)
      return { type: 'invalid' }
  }
}

type CreateDefaultDimensionValueChoicesFromIdOrValuesResult<
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
> =
  | {
      type: 'invalid'
      dimension?: never
      dimensionChoice?: never
      dimensionValueChoice?: never
    }
  | {
      type: 'dimension'
      dimension: DataKey
      dimensionChoice?: never
      dimensionValueChoice?: never
    }
  | {
      type: 'dimensions'
      dimension?: never
      dimensionChoice: ChartDimensionChoice<DataKey, DataRow>
      dimensionValueChoice?: never
    }
  | {
      type: 'value'
      dimension?: never
      dimensionChoice?: never
      dimensionValueChoice: ChartDimensionValueChoice<DataKey, DataRow>
    }
  | {
      type: 'valueOf'
      dimension?: never
      dimensionChoice: ChartDimensionChoice<DataKey, DataRow>
      dimensionValueChoice: ChartDimensionValueOfChoice<DataKey, DataRow>
    }

const checkExistingDimensionValueChoices = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  parentKey: DataKey,
  dimensionIdOrValue:
    | DataKey
    | ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
    | ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
    | undefined,
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
): CreateDefaultDimensionValueChoicesFromIdOrValuesResult<DataKey, DataRow> | undefined => {
  if (typeof dimensionIdOrValue !== 'string') return undefined

  const id = generateChoiceId(parentKey, dimensionIdOrValue)
  if (id in otherChoicesMap) {
    const firstChoice = otherChoicesMap[id] as ChartChoice<DataKey, DataRow>
    if (firstChoice.type === 'value') {
      return { type: 'value', dimensionValueChoice: firstChoice }
    }
    if (firstChoice.type === 'valueOf') {
      const dimensionChoice = otherChoicesMap[firstChoice.dimension] as ChartDimensionChoice<DataKey, DataRow>
      return { type: 'valueOf', dimensionChoice, dimensionValueChoice: firstChoice }
    }
    return { type: 'invalid' }
  }

  return undefined
}

export const createDefaultDimensionValueChoicesFromIdOrValues = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  parentKey,
  choiceAxis,
  metrics,
  dimensionIdOrValue,
  dimensionsMap,
  dimensionsValuesMap,
  otherChoicesMap,
  data,
  chartAvailableColors
}: {
  parentKey: DataKey
  choiceAxis: ChartChoiceChartAxis
  metrics: DataKey[] | undefined
  dimensionIdOrValue:
    | DataKey
    | ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'fixed'>
    | ChartDimensionValue<'props', DataKey, DataRow, 'optionalWithCallback', 'variable'>
    | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
  data: DataRow[]
  chartAvailableColors: readonly ThemeColor[]
}): CreateDefaultDimensionValueChoicesFromIdOrValuesResult<DataKey, DataRow> => {
  const existingDimensionValueChoices = checkExistingDimensionValueChoices(
    parentKey,
    dimensionIdOrValue,
    otherChoicesMap
  )
  if (existingDimensionValueChoices != null) {
    return existingDimensionValueChoices
  }

  if (typeof dimensionIdOrValue === 'string') {
    dimensionIdOrValue = getDimensionValueFromId(dimensionIdOrValue, dimensionsMap)
  }

  if (dimensionIdOrValue == null) {
    return { type: 'invalid' }
  }

  const type = dimensionIdOrValue.type
  switch (type) {
    case 'value':
      return createDefaultDimensionValueChoicesFromValues({
        parentKey,
        choiceAxis,
        metrics,
        dimensionValue: dimensionIdOrValue,
        dimensionsMap,
        dimensionsValuesMap,
        data,
        chartAvailableColors
      })
    case 'valueOf':
      return createDefaultDimensionValueChoicesFromValuesOf({
        parentKey,
        choiceAxis,
        metrics,
        dimensionValueOf: dimensionIdOrValue,
        dimensionsMap,
        dimensionsValuesMap,
        data,
        chartAvailableColors
      })
    default:
      assertNever(type)
      return { type: 'invalid' }
  }
}

export const populateChoices = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  id: DataKey,
  choices: Array<ChartChoice<DataKey, DataRow>>,
  choicesList: DataKey[],
  partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>>
): void => {
  for (const choice of choices) {
    if (choice.id === id) {
      choicesList.push(choice.id)
    } else if (choice.id in partialChoicesMap) {
      continue
    }
    partialChoicesMap[choice.id] = choice
  }
}

const getDimensionChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimension: Dimension<'props', DataKey, DataRow>,
  data: DataRow[]
): Dimension<'choice', DataKey, DataRow> | undefined => {
  if (dimension.type === 'string' && typeof dimension.descriptions === 'function') {
    let descriptions: Record<string, BaseElementDescription> | undefined
    try {
      descriptions = dimension.descriptions(dimension.id, data)
    } catch (error) {
      errorLocal(`getDimensionChoice ${dimension.id} descriptions callback error`, error)
    }
    if (descriptions == null) {
      return undefined
    }
    return {
      ...dimension,
      descriptions
    }
  }
  return dimension as Dimension<'choice', DataKey, DataRow>
}

/**
 * Generate the metrics to be kept from the Metric or Metrics Choice.
 *
 * All the metrics and variants options are kept, so that selection changes won't trigger data re-generation.
 */
const generateGroupByAndMetricsFromMetricOrMetrics = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionAxis,
  metricChoice,
  choicesMap,
  dimensionsMap,
  metrics
}: {
  dimensionAxis: DimensionAxisType
  metricChoice: ChartMetricChoice<DataKey, DataRow> | ChartMetricsChoice<DataKey, DataRow>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metrics: DataKey[]
}): void => {
  const {
    variant,
    change: { options: metricsOptions }
  } = metricChoice

  if (variant == null) {
    metrics.push(...metricsOptions.map(({ value }) => value))
  } else {
    const {
      axis,
      dimension,
      change: { options: variantOptions }
    } = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>

    if (axis === dimensionAxis) {
      metrics.push(...metricsOptions.map(({ value }) => value))
      return
    }

    const { newMetrics, variantsMap } = dimensionsMap[dimension] as MetricsVariants<'choice', DataKey, DataRow>

    for (const { value: metric } of metricsOptions) {
      if (metric in newMetrics) {
        metrics.push(...variantOptions.map(({ value }) => variantsMap[value][metric] as DataKey))
      } else {
        metrics.push(metric)
      }
    }
  }
}

const getTransposeAndSortInfoFromTranspose = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  transpose,
  choicesMap,
  selections
}: {
  transpose: DataKey
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
}): {
  transposeDimension: DataKey
  transposeOptions: ChartChoiceOptions<string> | undefined
  transposeOthers: boolean
  sort: DimensionValuesSortType | undefined
} => {
  let transposeDimension: DataKey = transpose
  let transposeOptions: ChartChoiceOptions<string> | undefined
  let transposeOthers = false
  let sort: DimensionValuesSortType | undefined

  const selected = new Set(selections[transpose] as string[])

  if (transpose in choicesMap) {
    const transposeChoice = choicesMap[transpose] as
      | ChartDimensionValuesChoice<DataKey, DataRow>
      | ChartDimensionValuesOfChoice<DataKey, DataRow>
      | ChartDimensionChoice<DataKey, DataRow>

    const { id: transposeId, type, change } = transposeChoice
    switch (type) {
      case 'values': {
        transposeDimension = transposeChoice.dimension
        transposeOptions = change.options.filter(({ value }) => selected.has(value))
        transposeOthers = selections[generateIdForSelectionOthers(transposeId)] === SelectedOthers
        sort = typeof transposeChoice.sort === 'function' ? undefined : transposeChoice.sort
        break
      }
      case 'valuesOf': {
        transposeDimension = (selections[transposeChoice.dimension] as DataKey) ?? transposeChoice.dimension
        const selected = new Set(selections[transposeId] as string[])
        transposeOptions = change.options[transposeDimension].filter(({ value }) => selected.has(value))
        transposeOthers = selections[generateIdForSelectionOthers(transposeId)] === SelectedOthers
        sort = typeof transposeChoice.sort === 'function' ? undefined : transposeChoice.sort
        break
      }
      case 'dimension':
        transposeDimension = selections[transposeChoice.id] as DataKey
        break
      default:
        assertNever(type)
    }
  }

  return { transposeDimension, transposeOptions, transposeOthers, sort }
}

/**
 * Generate the metrics to be kept from the Metric Choice.
 *
 * Considring that transposing generates a lot of new metrics, only the selected metric is kept.
 *
 * This means that the selection changes will trigger data re-generation.
 */
const generateGroupByAndMetricsFromMetricTransposed = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionAxis,
  metricChoice,
  choicesMap,
  dimensionsMap,
  selections,
  metrics,
  transposeMetrics
}: {
  dimensionAxis: DimensionAxisType
  metricChoice: ChartMetricTransposedChoice<DataKey, DataRow>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
  metrics: DataKey[]
  transposeMetrics: Array<TransposeMetric<DataKey>>
}): void => {
  const { id: choiceId, transpose, variant } = metricChoice
  const { transposeDimension, transposeOptions, transposeOthers, sort } = getTransposeAndSortInfoFromTranspose({
    transpose,
    choicesMap,
    selections
  })

  const selectedMetric = selections[choiceId] as DataKey

  let metricId: DataKey = selectedMetric

  if (variant != null) {
    const { axis, dimension } = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>
    if (axis !== dimensionAxis) {
      const variantValue = selections[variant] as string

      const { newMetrics, variantsMap } = dimensionsMap[dimension] as MetricsVariants<'choice', DataKey, DataRow>

      if (selectedMetric in newMetrics && !(variantValue in variantsMap)) {
        errorLocal(`generateGroupByAndMetricsFromTransposed ${choiceId} ${variantValue} not in variantsMap`, {
          variant,
          choiceId,
          variantValue,
          variantsMap,
          selections
        })
      }

      metricId = (variantsMap[variantValue][selectedMetric] as DataKey) ?? selectedMetric
    }
  }

  metrics.push(metricId)

  transposeMetrics.push({
    type: 'multiple',
    choiceId,
    metric: metricId,
    dimension: transposeDimension,
    values: transposeOptions ?? [],
    sort,
    filterByValue:
      metricChoice.filter === true && transposeOptions != null && transposeOptions.length > 0 && !transposeOthers,
    others: transposeOthers
  })
}

const generateGroupByAndMetricsFromMetricVariantsTransposed = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>(
  metricChoice: ChartMetricVariantsTransposedChoice<DataKey, DataRow>,
  choicesMap: ChartChoicesMap<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  selections: ChartSelections<DataKey>,
  metrics: DataKey[],
  transposeMetrics: Array<TransposeMetric<DataKey>>
): void => {
  const { id: choiceId, transpose, variant } = metricChoice
  let transposeDimension: DataKey = transpose

  let transposeOption: ChartChoiceOption<string> | undefined

  const selected = selections[transpose] as string

  if (transpose in choicesMap) {
    const transposeChoice = choicesMap[transpose] as
      | ChartDimensionValueChoice<DataKey, DataRow>
      | ChartDimensionValueOfChoice<DataKey, DataRow>
      | ChartDimensionChoice<DataKey, DataRow>

    const { type, change } = transposeChoice

    switch (type) {
      case 'value':
        transposeDimension = transposeChoice.dimension
        transposeOption = change.options.find(({ value }) => value === selected)
        break
      case 'valueOf':
        transposeDimension = (selections[transposeChoice.dimension] as DataKey) ?? transposeChoice.dimension
        transposeOption = change.options[transposeDimension].find(({ value }) => value === selected)
        break
      case 'dimension':
        transposeDimension = selections[transposeChoice.id] as DataKey
        transposeOption = change.options.find(({ value }) => value === selected)
        break
      default:
        assertNever(type)
    }
  }

  const selectedMetric = selections[choiceId] as DataKey

  const { dimension } = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>
  const variantValues = selections[variant] as string[]
  const { variantsMap, descriptions } = dimensionsMap[dimension] as MetricsVariants<'choice', DataKey, DataRow>

  for (const variantValue of variantValues) {
    if (!(variantValue in variantsMap)) {
      errorLocal(`generateGroupByAndMetricsFromTransposed ${choiceId} ${variantValue} not in variantsMap`, {
        variant,
        choiceId,
        variantValue,
        variantsMap,
        selections
      })
      continue
    }

    transposeOption = { ...transposeOption, value: selected, ...descriptions?.[variantValue] }

    const metricId = variantsMap[variantValue][selectedMetric] as DataKey

    metrics.push(metricId)
    transposeMetrics.push({
      type: 'single',
      choiceId,
      metric: metricId,
      dimension: transposeDimension,
      value: transposeOption,
      filterByValue: metricChoice.filter === true
    })
  }
}

/**
 * Generate the metrics to be kept from the Metric Variants Choice.
 *
 * All the variants options are kept, so that selection changes won't trigger data re-generation.
 */
const generateGroupByAndMetricsFromMetricVariants = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  firstMetricChoice: ChartMetricVariantsChoice<DataKey, DataRow>,
  choicesMap: ChartChoicesMap<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  metrics: DataKey[],
  selections: ChartSelections<DataKey>
): void => {
  const {
    id: metricChoiceId,
    variant,
    change: { options: metricsOptions }
  } = firstMetricChoice

  const metricsChosen =
    metricChoiceId in selections ? [selections[metricChoiceId] as DataKey] : metricsOptions.map(({ value }) => value)

  const {
    id: variantValueChoiceId,
    dimension,
    change: { options: variantOptions }
  } = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>

  const variantValues = (selections[variantValueChoiceId] ?? variantOptions.map(({ value }) => value)) as string[]

  const { variantsMap } = dimensionsMap[dimension] as MetricsVariants<'choice', DataKey, DataRow>

  for (const metric of metricsChosen) {
    metrics.push(...variantValues.map((value) => variantsMap[value][metric] as DataKey))
  }
}

const generateGroupByAndMetricsForMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionAxis,
  axisDataKey,
  choicesMap,
  dimensionsMap,
  metrics,
  transposeMetrics,
  selections
}: {
  dimensionAxis: DimensionAxisType
  axisDataKey: DataKey
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metrics: DataKey[]
  transposeMetrics: Array<TransposeMetric<DataKey>>
  selections: ChartSelections<DataKey>
}): void => {
  const metricChoice = choicesMap[axisDataKey] as
    | ChartMetricChoice<DataKey, DataRow>
    | ChartMetricVariantsChoice<DataKey, DataRow>
    | ChartMetricTransposedChoice<DataKey, DataRow>
    | ChartMetricsChoice<DataKey, DataRow>
    | undefined

  if (metricChoice == null) {
    metrics.push(axisDataKey)
    return
  }

  const { type } = metricChoice

  switch (type) {
    case 'metric':
      generateGroupByAndMetricsFromMetricOrMetrics({ dimensionAxis, metricChoice, choicesMap, dimensionsMap, metrics })
      break
    case 'metricTransposed':
      generateGroupByAndMetricsFromMetricTransposed({
        dimensionAxis,
        metricChoice,
        choicesMap,
        dimensionsMap,
        selections,
        metrics,
        transposeMetrics
      })
      break
    case 'metricVariants':
      if (metricChoice.transpose == null) {
        generateGroupByAndMetricsFromMetricVariants(metricChoice, choicesMap, dimensionsMap, metrics, selections)
      } else {
        generateGroupByAndMetricsFromMetricVariantsTransposed(
          metricChoice as ChartMetricVariantsTransposedChoice<DataKey, DataRow>,
          choicesMap,
          dimensionsMap,
          selections,
          metrics,
          transposeMetrics
        )
      }
      break
    case 'metrics':
      generateGroupByAndMetricsFromMetricOrMetrics({ dimensionAxis, metricChoice, choicesMap, dimensionsMap, metrics })
      break
    default:
      assertNever(type)
  }
}

interface GroupByAndMetricsResult<DataKey extends string> {
  groupBy: DataKey[]
  metrics: DataKey[]
  transposeMetrics: Array<TransposeMetric<DataKey>>
}

export const generateGroupByAndMetricsFromChartDefinition = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  chart,
  choicesMap,
  dimensionsMap,
  selections
}: {
  chart: ChartCardChartDefinition<DataKey, DataRow>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
}): GroupByAndMetricsResult<DataKey> => {
  const { firstAxis, secondAxis, otherSecondAxis, thirdAxis, dimensionAxis } = chart

  const groupBy: DataKey[] = []
  const metrics: DataKey[] = []
  const transposeMetrics: Array<TransposeMetric<DataKey>> = []

  const dimension = dimensionAxis === 'third' ? thirdAxis : firstAxis
  const dimensionChoice = choicesMap[dimension] as ChartDimensionAxisChoice<DataKey, DataRow> | undefined

  if (dimensionChoice == null) {
    groupBy.push(dimension)
  } else {
    const type = dimensionChoice.type

    switch (type) {
      case 'values':
        groupBy.push(dimensionChoice.dimension)
        break
      case 'valuesOf':
        groupBy.push(selections[dimensionChoice.dimension] as DataKey)
        break
      case 'variantValues':
        groupBy.push(dimensionChoice.dimension)
        break
      default:
        assertNever(type)
    }
  }

  const firstMetricAxis = dimensionAxis === 'third' ? firstAxis : secondAxis
  const secondMetricAxis = dimensionAxis === 'third' ? secondAxis : otherSecondAxis

  generateGroupByAndMetricsForMetric({
    dimensionAxis,
    axisDataKey: firstMetricAxis,
    choicesMap,
    dimensionsMap,
    metrics,
    transposeMetrics,
    selections
  })

  if (secondMetricAxis != null) {
    generateGroupByAndMetricsForMetric({
      dimensionAxis,
      axisDataKey: secondMetricAxis,
      choicesMap,
      dimensionsMap,
      metrics,
      transposeMetrics,
      selections
    })
  }

  return { groupBy, metrics, transposeMetrics }
}

export const generateGroupByAndMetricsFromColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columns,
  dimensionsMap,
  metricsMap,
  selections
}: {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
}): GroupByAndMetricsResult<DataKey> => {
  const selectedColumnIds = selections['columns' as DataKey] as string[] | undefined
  const groupBy: DataKey[] = []
  const metrics: DataKey[] = []

  const dataKeys =
    selectedColumnIds == null
      ? columns.map((column) => getColumnDataKey<DataKey, DataRow>(column))
      : columns
          .filter(isColumnWithAccessorKey)
          .filter((column) => selectedColumnIds.includes(getColumnId(column)))
          .map((column) => getColumnDataKey<DataKey, DataRow>(column))

  for (const dataKey of dataKeys) {
    if (dataKey == null) {
      continue
    }
    if (dimensionsMap[dataKey] != null) {
      groupBy.push(dataKey)
    } else if (metricsMap[dataKey] != null) {
      metrics.push(dataKey)
    }
  }

  return { groupBy, metrics, transposeMetrics: [] }
}

const getTransposeOptionsFromDimension = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimension: Dimension<'choice', DataKey, DataRow>,
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
): ChartChoiceOptions<string> => {
  const { id, descriptions: valuesDescriptionsMap, othersDescription: othersDefinition } = dimension
  const transposeOptions: ChartChoiceOptions<string> = []

  if (valuesDescriptionsMap != null) {
    for (const value of dimensionsValuesMap[id]) {
      const valueDefinition = valuesDescriptionsMap[value]
      if (valueDefinition != null) {
        transposeOptions.push({ value, ...valueDefinition })
      } else {
        transposeOptions.push({ value, ...othersDefinition })
      }
    }
  } else {
    for (const value of dimensionsValuesMap[id]) {
      transposeOptions.push({ value })
    }
  }

  return transposeOptions
}

const getTransposeDimensionsAndOptions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  transpose: DataKey,
  choicesMap: ChartChoicesMap<DataKey, DataRow>
): {
  transposeDimensions: DataKey[]
  transposeOptionsOr?: ChartChoiceOptions<string> | ChartChoiceOptionsOf<DataKey, string>
  transposeFilter?: MetricFilterType
} => {
  const transposeChoice = choicesMap[transpose] as
    | ChartDimensionValuesChoice<DataKey, DataRow>
    | ChartDimensionValuesOfChoice<DataKey, DataRow>
    | ChartDimensionValueChoice<DataKey, DataRow>
    | ChartDimensionValueOfChoice<DataKey, DataRow>
    | ChartDimensionChoice<DataKey, DataRow>
    | undefined

  if (transposeChoice == null) {
    return { transposeDimensions: [transpose] }
  }

  const { type, change } = transposeChoice
  switch (type) {
    case 'value':
    case 'values':
      return {
        transposeDimensions: [transposeChoice.dimension],
        transposeOptionsOr: change.options,
        transposeFilter: type === 'values' ? (transposeChoice.filter as MetricFilterType) : undefined
      }
    case 'valueOf':
    case 'valuesOf': {
      const dimensionChoice = choicesMap[transposeChoice.dimension] as ChartDimensionChoice<DataKey, DataRow>
      return {
        transposeDimensions: dimensionChoice.change.options.map(({ value }) => value),
        transposeOptionsOr: change.options,
        transposeFilter: type === 'valuesOf' ? (transposeChoice.filter as MetricFilterType) : undefined
      }
    }
    case 'dimension':
      return {
        transposeDimensions: transposeChoice.change.options.map(({ value }) => value)
      }
    default:
      assertNever(type)
  }

  return { transposeDimensions: [transpose] }
}

const generateAllTransposedNoVariantMetricsDefinitions = <
  DataKey extends string,
  DataRow extends ChartDataRow<DataKey>
>({
  choice,
  dimensionsMap,
  metricsMap,
  dimensionsValuesMap,
  metricIds,
  transposeDimensions,
  transposeOptionsOr,
  transposeFilter,
  elementsDescriptionsMap
}: {
  choice: ChartMetricTransposedChoice<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  metricIds: Set<DataKey>
  transposeDimensions: DataKey[]
  transposeOptionsOr: ChartChoiceOptions<string> | ChartChoiceOptionsOf<DataKey, string> | undefined
  transposeFilter: MetricFilterType | undefined
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
}): void => {
  // Transpose a standard metric
  const { color, pattern } = choice
  for (const dimensionId of transposeDimensions) {
    const dimensionn = dimensionsMap[dimensionId] as Dimension<'choice', DataKey, DataRow>
    let transposeOptions: ChartChoiceOptions<string> | undefined
    if (transposeOptionsOr == null) {
      transposeOptions = getTransposeOptionsFromDimension(dimensionn, dimensionsValuesMap)
    } else {
      transposeOptions = Array.isArray(transposeOptionsOr) ? transposeOptionsOr : transposeOptionsOr[dimensionId]
    }
    for (const { value, ...transposeOption } of transposeOptions) {
      for (const metricId of metricIds) {
        const { type, format } = metricsMap[metricId] as MetricChoice<DataKey, DataRow>
        elementsDescriptionsMap[transposedMetricColumn(metricId, value)] = {
          type,
          format: format as ElementDescriptionMetric['format'],
          filter: transposeFilter,
          color,
          pattern,
          ...transposeOption
        }
      }
    }
  }
}

const transposeVariantNewMetricIds = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  variant: DataKey,
  choicesMap: ChartChoicesMap<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>,
  metricIds: Set<DataKey>,
  variantMetricsDefinition: DataKeyMap<DataKey, BaseElementDescription>
): void => {
  const { dimension: variantId, change } = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>
  const variantValues = change.options.map(({ value }) => value as DataKey)

  const { newMetrics, variantsMap, descriptions } = dimensionsMap[variantId] as MetricsVariants<
    'choice',
    DataKey,
    DataRow
  >

  for (const metricId of metricIds) {
    if (metricId in newMetrics) {
      metricIds.delete(metricId)
      for (const value of variantValues) {
        const newMetricId = variantsMap[value][metricId] as DataKey
        metricIds.add(newMetricId)
        variantMetricsDefinition[newMetricId] = getMetricDescription(
          descriptions?.[value],
          newMetrics[metricId],
          metricsMap[newMetricId]
        )
      }
    }
  }
}

const generateTransposedOptionsMetricDefinitions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  transposeOptions,
  transposeFilter,
  metricIds,
  metricsMap,
  elementsDescriptionsMap,
  variantMetricsDefinition
}: {
  choice: ChartMetricVariantsTransposedChoice<DataKey, DataRow> | ChartMetricTransposedChoice<DataKey, DataRow>
  transposeOptions: ChartChoiceOptions<string>
  transposeFilter: MetricFilterType | undefined
  metricIds: Set<DataKey>
  metricsMap: MetricsMap<DataKey, DataRow>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
  variantMetricsDefinition: DataKeyMap<DataKey, BaseElementDescription>
}): void => {
  const { type: choiceType, color, pattern, variant } = choice
  for (const { value, ...transposeOption } of transposeOptions) {
    for (const metricId of metricIds) {
      const { type, format } = metricsMap[metricId] as MetricChoice<DataKey, DataRow>
      if (choiceType === 'metricVariants') {
        elementsDescriptionsMap[transposedMetricColumn(metricId, value)] = {
          type,
          format: format as ElementDescriptionMetric['format'],
          filter: transposeFilter,
          ...getMetricDescription(variantMetricsDefinition[metricId], transposeOption)
        }
      } else {
        elementsDescriptionsMap[transposedMetricColumn(metricId, value)] = {
          type,
          format: format as ElementDescriptionMetric['format'],
          filter: transposeFilter,
          color,
          pattern,
          variant,
          ...getMetricDescription(transposeOption)
        }
      }
    }
  }
}

const generateAllTransposedVariantMetricsDefinitions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  variant,
  choicesMap,
  dimensionsMap,
  metricsMap,
  dimensionsValuesMap,
  metricIds,
  transposeDimensions,
  transposeOptionsOr,
  transposeFilter,
  elementsDescriptionsMap
}: {
  choice: ChartMetricVariantsTransposedChoice<DataKey, DataRow>
  variant: DataKey
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  metricIds: Set<DataKey>
  transposeDimensions: DataKey[]
  transposeOptionsOr: ChartChoiceOptions<string> | ChartChoiceOptionsOf<DataKey, string> | undefined
  transposeFilter: MetricFilterType | undefined
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
}): void => {
  const variantMetricsDefinition: DataKeyMap<DataKey, BaseElementDescription> = {}

  transposeVariantNewMetricIds(variant, choicesMap, dimensionsMap, metricsMap, metricIds, variantMetricsDefinition)

  for (const dimensionId of transposeDimensions) {
    const dimension = dimensionsMap[dimensionId] as Dimension<'choice', DataKey, DataRow>
    let transposeOptions: ChartChoiceOptions<string> | undefined
    if (transposeOptionsOr == null) {
      transposeOptions = getTransposeOptionsFromDimension(dimension, dimensionsValuesMap)
    } else {
      transposeOptions = Array.isArray(transposeOptionsOr) ? transposeOptionsOr : transposeOptionsOr[dimensionId]
    }
    generateTransposedOptionsMetricDefinitions({
      choice,
      transposeOptions,
      transposeFilter,
      metricIds,
      metricsMap,
      elementsDescriptionsMap,
      variantMetricsDefinition
    })
  }
}

export const generateAllTransposedMetricsDefinitions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  metricsMap,
  dimensionsMap,
  dimensionsValuesMap,
  choicesMap,
  elementsDescriptionsMap
}: {
  choice: ChartMetricTransposedChoice<DataKey, DataRow> | ChartMetricVariantsTransposedChoice<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  dimensionsValuesMap: DimensionsValuesMap<DataKey>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
}): void => {
  const { transpose, variant, change } = choice

  const { transposeDimensions, transposeOptionsOr, transposeFilter } = getTransposeDimensionsAndOptions(
    transpose,
    choicesMap
  )

  const metricIds = new Set<DataKey>(change.options.map(({ value }) => value))

  if (variant == null) {
    // Transpose a standard metric
    generateAllTransposedNoVariantMetricsDefinitions({
      choice: choice as ChartMetricTransposedChoice<DataKey, DataRow>,
      dimensionsMap,
      metricsMap,
      dimensionsValuesMap,
      metricIds,
      transposeDimensions,
      transposeOptionsOr,
      transposeFilter,
      elementsDescriptionsMap
    })
  } else {
    // Transpose a variant metric
    generateAllTransposedVariantMetricsDefinitions({
      choice: choice as ChartMetricVariantsTransposedChoice<DataKey, DataRow>,
      variant,
      choicesMap,
      dimensionsMap,
      metricsMap,
      dimensionsValuesMap,
      metricIds,
      transposeDimensions,
      transposeOptionsOr,
      transposeFilter,
      elementsDescriptionsMap
    })
  }
}

const getMetricDescription = (
  first: BaseElementDescription | undefined,
  second?: BaseElementDescription,
  third?: BaseElementDescription
): BaseElementDescription => {
  const label = first?.label ?? second?.label ?? third?.label
  const description = first?.description ?? second?.description ?? third?.color
  const color = first?.color ?? second?.color ?? third?.color
  const pattern = first?.pattern ?? second?.pattern ?? third?.pattern
  const alert = first?.alert ?? second?.alert ?? third?.alert
  const alertText = first?.alertText ?? second?.alertText ?? third?.alertText
  return {
    ...(label == null ? {} : { label }),
    ...(description == null ? {} : { description }),
    ...(color == null ? {} : { color }),
    ...(pattern == null ? {} : { pattern }),
    ...(alert == null ? {} : { alert }),
    ...(alertText == null ? {} : { alertText })
  }
}

export const generateAllVariantMetricsDefinitions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice: ChartMetricVariantsChoice<DataKey, DataRow> | ChartMetricChoice<DataKey, DataRow>,
  choicesMap: ChartChoicesMap<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  metricsMap: MetricsMap<DataKey, DataRow>,
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
): void => {
  const { variant: variantChoiceId, change } = choice
  if (variantChoiceId == null) {
    return
  }

  if (!(variantChoiceId in choicesMap)) {
    errorLocal(`generateAllVariantMetricsDefinitions variantChoice ${variantChoiceId} not in choicesMap`, {
      variantChoiceId,
      choicesMap
    })
    return
  }
  const { dimension: variantDimensionId } = choicesMap[variantChoiceId] as
    | ChartVariantValuesChoice<DataKey, DataRow>
    | ChartVariantValueChoice<DataKey, DataRow>

  if (!(variantDimensionId in dimensionsMap)) {
    errorLocal(`generateAllVariantMetricsDefinitions variantId ${variantChoiceId} not in dimensionsMap`, {
      variantId: variantDimensionId,
      variantChoiceId,
      dimensionsMap
    })
    return
  }

  const { variantsMap } = dimensionsMap[variantDimensionId] as MetricsVariants<'choice', DataKey, DataRow>
  const choiceVariantValues = choicesMap[variantChoiceId] as
    | ChartVariantValuesChoice<DataKey, DataRow>
    | ChartVariantValueChoice<DataKey, DataRow>
  const variantValuesOptions = choiceVariantValues?.change?.options ?? change.options

  const { options: newMetricOptions } = change
  for (const { value: newMetricId, ...newMetricDescription } of newMetricOptions) {
    for (const { value: variantValue, ...valueDescription } of variantValuesOptions) {
      if (!(variantValue in variantsMap)) {
        errorLocal(`generateAllVariantMetricsDefinitions variantValue ${variantValue} not in variantsMap`, {
          choice,
          choicesMap,
          newMetricId,
          newMetricOptions,
          variantValuesOptions,
          variantValue,
          variantsMap,
          change
        })
        continue
      }
      const metricId = variantsMap[variantValue][newMetricId] as DataKey
      const { type: metricType, format, ...metricDescription } = metricsMap[metricId] as MetricChoice<DataKey, DataRow>
      elementsDescriptionsMap[metricId] = {
        type: metricType,
        format: format as ElementDescriptionMetric['format'],
        ...getMetricDescription(valueDescription, newMetricDescription, metricDescription),
        variant: variantChoiceId
      }
    }
  }
}

export const createDimensionsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimensions: Array<Dimension<'props', DataKey, DataRow>> | undefined,
  data: DataRow[] | null | undefined
): DimensionsMap<DataKey, DataRow> | undefined => {
  if (data == null || dimensions == null) {
    return undefined
  }

  const dimensionsMap: DimensionsMap<DataKey, DataRow> = {}
  for (const dimension of dimensions) {
    if (dimension.id in dimensionsMap) {
      errorLocal(`createDimensionsMap ${dimension.id} already in map`, dimensionsMap)
      return undefined
    }
    const dimensionChoice = getDimensionChoice<DataKey, DataRow>(dimension, data)
    if (dimensionChoice == null) {
      return undefined
    }
    dimensionsMap[dimension.id] = dimensionChoice
  }

  return dimensionsMap
}

const isSymbolNode = (node: MathNode): node is SymbolNode => node.type === 'SymbolNode'

/**
 * Extract variable names from a formula string
 *
 * Uses mathjs to parse the formula and extract the variable names
 *
 * @param formula - Formula string (e.g., "conversion_value / cost")
 * @returns Array of unique variable names referenced in the formula
 *
 * @example
 * extractVariables("conversion_value / cost")
 * // Returns: ["conversion_value", "cost"]
 *
 * extractVariables("(revenue - cost) / impressions * 100")
 * // Returns: ["revenue", "cost", "impressions"]
 */
export const extractFormulaVariables = (formula: string): string[] => {
  const node = parse(formula)
  const symbols: SymbolNode[] = node.filter(isSymbolNode) as SymbolNode[]
  return Array.from(new Set(symbols.map((n) => n.name)))
}

const createMetricChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  metric: Metric<DataKey, DataRow>
): MetricChoice<DataKey, DataRow> => {
  const { type, formula: formulaProp } = metric

  let sameAsMetric = true
  let format = metric.format
  let formulaMetrics: DataKey[] | null | undefined

  if (type === 'currency' && metric.currencyCode != null && typeof format !== 'function') {
    format = { ...format, currencyCode: metric.currencyCode }
    sameAsMetric = false
  }

  let formula: MetricChoice<DataKey, DataRow>['formula']
  if (typeof formulaProp === 'string') {
    sameAsMetric = false
    formula = compile(formulaProp)
    formulaMetrics = extractFormulaVariables(formulaProp) as DataKey[]
  } else {
    formula = formulaProp
  }

  if (sameAsMetric) {
    return metric as MetricChoice<DataKey, DataRow>
  }

  const metricChoice: MetricChoice<DataKey, DataRow> = { ...metric, formula }
  if (format != null) {
    metricChoice.format = format
  }
  if (formulaMetrics != null) {
    metricChoice.formulaMetrics = formulaMetrics
  }

  return metricChoice
}

const isValidMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  metricsMap: MetricsMap<DataKey, DataRow>,
  dimensionsMap: DimensionsMap<DataKey, DataRow>,
  metric: Metric<DataKey, DataRow>
): boolean => {
  const { id, formula } = metric

  if (id in metricsMap || id in dimensionsMap) {
    return false
  }

  if (typeof formula === 'string') {
    try {
      validateFormula(formula)
    } catch (error) {
      if (error instanceof Error) {
        errorLocal(`createMetricsMap ${id} formula invalid: ${error.message}`, { formula })
      } else {
        errorLocal(`createMetricsMap ${id} formula invalid`, { formula })
      }
      return false
    }
  }

  return true
}

export const createMetricsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  metrics: Array<Metric<DataKey>> | undefined,
  dimensionsMap: DimensionsMap<DataKey, DataRow> | undefined
): MetricsMap<DataKey, DataRow> | undefined => {
  if (dimensionsMap == null || metrics == null) {
    return undefined
  }
  const metricsMap: MetricsMap<DataKey, DataRow> = {}
  for (const metric of metrics) {
    const { id } = metric
    if (isValidMetric(metricsMap, dimensionsMap, metric)) {
      metricsMap[id] = createMetricChoice<DataKey, DataRow>(metric)
    } else {
      return undefined
    }
  }

  const stringIsMetricDataKey = (value: string): value is DataKey => value in metricsMap

  for (const metric of objectValues(metricsMap) as Array<MetricChoice<DataKey, DataRow>>) {
    if (metric.formulaMetrics != null) {
      metric.formulaMetrics = metric.formulaMetrics.filter<DataKey>(stringIsMetricDataKey)
    }
  }

  return metricsMap
}

const isVariant = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimension: Dimension<'props', DataKey, DataRow>
): dimension is MetricsVariants<'props', DataKey, DataRow> => dimension.type === 'variant'

export const createMetricsVariantsMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimensions: Array<Dimension<'props', DataKey, DataRow>> | undefined
): MetricsVariantsMap<DataKey> | undefined => {
  if (dimensions == null) {
    return undefined
  }

  const metricsVariantsMap: MetricsVariantsMap<DataKey> = {}
  const variants = dimensions
    ?.filter((item) => isVariant<DataKey, DataRow>(item))
    ?.flatMap(({ id, newMetrics }) => objectKeys(newMetrics).map<[DataKey, DataKey]>((key) => [key, id]))

  for (const [key, dimension] of variants) {
    if (key in metricsVariantsMap) {
      errorLocal(`useValidateChartProps metricsVariantMap ${key} already in map`, metricsVariantsMap)
      return undefined
    }
    metricsVariantsMap[key] = dimension
  }

  return metricsVariantsMap
}

export const createOtherChoicesMap = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  ...choicesLists: Array<Array<ChartChoice<DataKey, DataRow>> | undefined>
): Partial<ChartChoicesMap<DataKey, DataRow>> => {
  const otherChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>> = {}
  for (const choiceList of choicesLists) {
    if (choiceList != null) {
      for (const choice of choiceList) {
        otherChoicesMap[choice.id] = choice
      }
    }
  }
  return otherChoicesMap
}

export const getChartDimensionAndChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chart,
  choicesMap,
  dimensionsMap,
  selections
}: {
  chart: ChartCardChartDefinition<DataKey, DataRow> | undefined
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
}): {
  dimension: Dimension<'choice', DataKey, DataRow> | undefined
  choice: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
} => {
  if (chart == null) {
    return { dimension: undefined, choice: undefined }
  }
  const { firstAxis, thirdAxis, dimensionAxis } = chart
  const axisKey = dimensionAxis === 'third' ? thirdAxis : firstAxis

  const choice = choicesMap[axisKey] as ChartDimensionAxisChoice<DataKey, DataRow> | undefined

  if (choice == null) {
    return { dimension: dimensionsMap[axisKey], choice }
  }

  if (choice.type === 'valuesOf') {
    return { dimension: dimensionsMap[selections[choice.dimension] as DataKey], choice }
  }

  return { dimension: dimensionsMap[choice.dimension], choice }
}

const getOptionsFromColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columns,
  dimensionsMap,
  metricsMap,
  columnsIds
}: {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  columnsIds: DataKey[] | 'all'
}): Array<ChartChoiceOption<DataKey>> =>
  columns
    .filter((column) => {
      if (columnsIds === 'all') {
        return true
      }
      const id = getColumnId(column)
      if (id == null) {
        return false
      }
      return columnsIds.includes(id as DataKey)
    })
    .map((column) => {
      const dataKey = getColumnDataKey<DataKey, DataRow>(column)
      const metaChartCard = column.meta?.chartCard ?? {}
      const id = getColumnId(column) as string
      const dimensionOrMetric = dataKey == null ? undefined : dimensionsMap[dataKey] ?? metricsMap[dataKey]
      const option: ChartChoiceOption<DataKey> = {
        value: id as DataKey,
        disabled: column.enableHiding === false,
        label: metaChartCard.label ?? dimensionOrMetric?.label ?? id,
        description: metaChartCard.description ?? dimensionOrMetric?.description,
        color: metaChartCard.color ?? dimensionOrMetric?.color,
        pattern: metaChartCard.pattern ?? dimensionOrMetric?.pattern
      }
      return option
    })

const dateSortingFn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  rowA: Row<DataRow>,
  rowB: Row<DataRow>,
  columnId: string
): number => {
  const valueA = rowA.getValue(columnId) as string | null | undefined
  const valueB = rowB.getValue(columnId) as string | null | undefined
  if (valueA == null) {
    return valueB == null ? 0 : 1
  }
  if (valueB == null) {
    return -1
  }
  return new Date(valueA).getTime() - new Date(valueB).getTime()
}

const getDimensionOrMetricSortingFn = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionOrMetric
}: {
  dimensionOrMetric: Dimension<'choice', DataKey, DataRow> | MetricChoice<DataKey, DataRow>
}): SortingFnOption<DataRow> => {
  const { type } = dimensionOrMetric
  switch (type) {
    case 'date':
      return dateSortingFn
    case 'string':
    case 'variant': {
      const descriptions = dimensionOrMetric.descriptions
      if (descriptions == null) {
        return 'alphanumeric'
      }
      return (rowA, rowB, columnId) => {
        let valueA = rowA.getValue(columnId) as string | null | undefined
        valueA = typeof valueA === 'string' ? descriptions[valueA]?.label ?? valueA : valueA
        let valueB = rowB.getValue(columnId) as string | null | undefined
        valueB = typeof valueB === 'string' ? descriptions[valueB]?.label ?? valueB : valueB
        if (valueA == null) {
          return valueB == null ? 0 : 1
        }
        if (valueB == null) {
          return -1
        }
        return valueA.localeCompare(valueB)
      }
    }
  }
  return 'alphanumeric'
}

const createTableColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columnsProp,
  dimensionsMap,
  metricsMap
}: {
  columnsProp: Array<DataKey | ChartCardTableColumnDef<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
}): Array<ChartCardTableColumnDef<DataKey, DataRow>> => {
  return columnsProp
    .map<ChartCardTableColumnDef<DataKey, DataRow>>((columnOrId) => {
      if (typeof columnOrId === 'string') {
        const defaultSortDirection = columnOrId in metricsMap ? 'desc' : 'asc'
        const filterFn = columnOrId in dimensionsMap ? oneOfFilter : undefined
        const column: ChartCardTableColumnDef<DataKey, DataRow> = {
          id: columnOrId,
          accessorKey: columnOrId,
          filterFn,
          meta: {
            chartCard: {
              defaultSortDirection
            }
          }
        }
        return column
      }
      return columnOrId
    })
    .map((column) => {
      const dataKey = getColumnDataKey<DataKey, DataRow>(column)
      if (dataKey == null) {
        return column
      }
      const dimensionOrMetric = dimensionsMap[dataKey] ?? metricsMap[dataKey]
      if (dimensionOrMetric == null) {
        return column
      }
      const filterFn = dataKey in dimensionsMap ? oneOfFilter : undefined
      const metaChartCard = column.meta?.chartCard ?? {}
      const defaultSortDirection = dataKey in metricsMap ? 'desc' : 'asc'
      // Formula metrics are computed client-side and are not analytics orderBy fields.
      const isCalculatedMetricColumn = elementIsMetric(dimensionOrMetric) && dimensionOrMetric.formula != null

      return {
        ...column,
        accessorKey: dataKey,
        ...(isCalculatedMetricColumn ? { enableSorting: false } : undefined),
        filterFn: column.filterFn ?? filterFn,
        sortingFn: column.sortingFn ?? getDimensionOrMetricSortingFn({ dimensionOrMetric }),
        footer:
          column.footer ??
          (elementIsMetric(dimensionOrMetric) ? getFooterMetricAggregation(dimensionOrMetric, metricsMap) : undefined),
        meta: {
          chartCard: {
            ...metaChartCard,
            label: metaChartCard.label ?? dimensionOrMetric?.label ?? column.id,
            description: metaChartCard.description ?? dimensionOrMetric?.description,
            color: metaChartCard.color ?? dimensionOrMetric?.color,
            pattern: metaChartCard.pattern ?? dimensionOrMetric?.pattern,
            descriptions:
              metaChartCard.descriptions ??
              (elementIsDimension(dimensionOrMetric) ? dimensionOrMetric.descriptions : undefined),
            defaultSortDirection: metaChartCard.defaultSortDirection ?? defaultSortDirection
          }
        }
      }
    })
}

const getDefaultColumnsChoiceFromColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columns: columnsProp,
  dimensionsMap,
  metricsMap,
  columnsIds,
  selected
}: {
  columns: Array<DataKey | ChartCardTableColumnDef<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  columnsIds: DataKey[] | 'all'
  selected?: DataKey[]
}): {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow>
} => {
  const columns = createTableColumns<DataKey, DataRow>({ columnsProp, dimensionsMap, metricsMap })

  const columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow> = {
    axis: 'second',
    chartType: 'table',
    id: 'columns' as DataKey,
    type: TableColumnsChoice,
    change: {
      change: 'internal',
      options: getOptionsFromColumns({ columns, dimensionsMap, metricsMap, columnsIds })
    },
    columns,
    ...(selected == null ? undefined : { selected })
  }

  return { columns, columnsChoice }
}

const getColumnsChoiceFromTableColumnsProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columnsProp: {
    id,
    columns: columnsProp,
    change: { options: optionsOr, ...changeProps },
    selected
  },
  dimensionsMap,
  metricsMap,
  data
}: {
  columnsProp: ChartCardTableColumnsProps<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  data: DataRow[]
}): {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow>
} => {
  const columns = createTableColumns<DataKey, DataRow>({ columnsProp, dimensionsMap, metricsMap })

  let options: Array<ChartChoiceOption<DataKey>> | undefined
  if (optionsOr === 'all') {
    options = getOptionsFromColumns<DataKey, DataRow>({ columns, dimensionsMap, metricsMap, columnsIds: 'all' })
  } else {
    const columnsIds = typeof optionsOr === 'function' ? optionsOr(data, id) : optionsOr
    options = getOptionsFromColumns<DataKey, DataRow>({ columns, dimensionsMap, metricsMap, columnsIds })
  }
  const columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow> = {
    id,
    axis: 'second',
    chartType: 'table',
    type: TableColumnsChoice,
    change: {
      options,
      ...changeProps
    },
    columns,
    ...(selected == null ? undefined : { selected })
  }

  return { columns, columnsChoice }
}

const getColumnsAndChoiceFromDimensionsAndMetrics = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimensionsMap,
  metricsMap
}: {
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
}): {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow>
} => {
  const dimensionColumns = strictObjectEntries(dimensionsMap)
    .filter(([_, { type }]) => type !== 'variant')
    .map<DataKey>(([id]) => id)

  const metricColumns = objectKeys(metricsMap)

  const columns = createTableColumns({
    columnsProp: [...dimensionColumns, ...metricColumns],
    dimensionsMap,
    metricsMap
  })

  const columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow> = {
    axis: 'second',
    chartType: 'table',
    id: 'columns' as DataKey,
    type: TableColumnsChoice,
    change: {
      change: 'internal',
      options: getOptionsFromColumns<DataKey, DataRow>({ columns, dimensionsMap, metricsMap, columnsIds: 'all' })
    },
    columns
  }

  return { columns, columnsChoice }
}

export const getColumnsAndChoiceFromColumnsProp = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columnsProp,
  dimensionsMap,
  metricsMap,
  data
}: {
  columnsProp: ColumnsProps<DataKey, DataRow> | undefined
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  data: DataRow[]
}): {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  columnsChoice: ChartCardTableColumnsChoice<DataKey, DataRow>
} => {
  if (Array.isArray(columnsProp)) {
    return getDefaultColumnsChoiceFromColumns<DataKey, DataRow>({
      columns: columnsProp,
      dimensionsMap,
      metricsMap,
      columnsIds: 'all'
    })
  }

  if (typeof columnsProp === 'object') {
    return getColumnsChoiceFromTableColumnsProps<DataKey, DataRow>({
      columnsProp,
      dimensionsMap,
      metricsMap,
      data
    })
  }

  return getColumnsAndChoiceFromDimensionsAndMetrics<DataKey, DataRow>({
    dimensionsMap,
    metricsMap
  })
}

export { elementDescriptionIsDimension, elementDescriptionIsMetric }

export const elementIsMetric = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  element?: Dimension<'choice', DataKey, DataRow> | MetricChoice<DataKey, DataRow>
): element is MetricChoice<DataKey, DataRow> => {
  if (element == null) {
    return false
  }
  const { type } = element
  switch (type) {
    case 'number':
    case 'currency':
    case 'percentage':
      return true
    case 'date':
    case 'string':
    case 'variant':
      return false
    default:
      assertNever(type)
      return false
  }
}

export const elementIsDimension = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  element?: Dimension<'choice', DataKey, DataRow> | MetricChoice<DataKey, DataRow>
): element is Dimension<'choice', DataKey, DataRow> => {
  if (element == null) {
    return false
  }
  const { type } = element
  switch (type) {
    case 'number':
    case 'currency':
    case 'percentage':
      return false
    case 'date':
    case 'string':
    case 'variant':
      return true
    default:
      assertNever(type)
      return false
  }
}

export const expandMetricsFromFormulas = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  metrics: DataKey[],
  metricsMap: MetricsMap<DataKey, DataRow>
): void => {
  const existingMetrics = new Set<DataKey>(metrics)
  const newMetrics = new Set<DataKey>()
  for (const metricId of metrics) {
    const { formulaMetrics } = metricsMap[metricId] ?? {}
    if (formulaMetrics == null) {
      continue
    }
    for (const formulaMetric of formulaMetrics) {
      newMetrics.add(formulaMetric)
    }
  }
  metrics.push(...setDifference(newMetrics, existingMetrics))
}

/**
 * Validate formula for security and complexity constraints
 *
 * Ensures formulas:
 * - Don't exceed length limits (prevents DoS from huge formulas)
 * - Don't have excessive operators (prevents complexity attacks)
 * - Don't have deep nesting (prevents stack overflow)
 * - Only contain allowed characters (identifiers, numbers, operators, whitespace)
 * - Don't contain function calls (only arithmetic allowed)
 *
 * @param formula - Formula string to validate
 * @throws Error if formula fails validation
 */
export const validateFormula = (formula: string): void => {
  // Check length
  if (formula.length > FORMULA_MAX_LENGTH) {
    throw new Error(`Formula exceeds maximum length of ${FORMULA_MAX_LENGTH} characters`)
  }

  // Check for disallowed characters (only allow identifiers, numbers, operators, whitespace)
  const allowedPattern = /^[a-zA-Z_0-9+\-*/().\s]+$/
  if (!allowedPattern.test(formula)) {
    throw new Error(
      'Formula contains disallowed characters. Only identifiers, numbers, and basic arithmetic operators (+, -, *, /, parentheses) are allowed'
    )
  }

  // Check for function calls - detect pattern: identifier followed by '('
  // This prevents things like: sqrt(x), pow(x,2), etc.
  const functionCallPattern = /[a-zA-Z_]\w*\s*\(/
  if (functionCallPattern.test(formula)) {
    throw new Error('Formula contains function calls. Only basic arithmetic operations are allowed')
  }

  // Count operators
  const operatorCount = (formula.match(/[+\-*/]/g) ?? []).length
  if (operatorCount > FORMULA_MAX_OPERATOR_COUNT) {
    throw new Error(`Formula exceeds maximum operator count of ${FORMULA_MAX_OPERATOR_COUNT}`)
  }

  // Check nesting depth (count max consecutive opening parentheses)
  let depth = 0
  let maxDepth = 0
  for (const char of formula) {
    if (char === '(') {
      depth++
      maxDepth = Math.max(maxDepth, depth)
    } else if (char === ')') {
      depth--
    }
  }
  if (maxDepth > FORMULA_MAX_NESTING_DEPTH) {
    throw new Error(`Formula exceeds maximum nesting depth of ${FORMULA_MAX_NESTING_DEPTH}`)
  }

  // Ensure balanced parentheses
  if (depth !== 0) {
    throw new Error('Formula has unbalanced parentheses')
  }
}
